import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Plus, Eye, Star, Search, X, Loader, BookOpen, Upload } from 'lucide-react';
import { IndianRupee } from '../../components/CustomIcons';
import { toast } from 'react-toastify';
import CurriculumModal from '../../components/CurriculumModal';
import PreviewCard from './PreviewCard';
import CommonToastContainer from '../../components/CommonToastContainer';
import { postRequest } from "../../utils/networking";
import { apiUrls, imageUrl } from "../../utils/url";

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }
  const asstesIndex = normalized.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return normalized.substring(asstesIndex + 7);
  }
  return normalized;
}

const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;

  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const extractedPath = getPath(imagePath);
  if (!extractedPath) return null;

  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imageUrl}/${cleanPath}`;
};

const CourseDetails = ({ course, onSave, onCancel, isNew, onViewCurriculum }) => {
  const [formData, setFormData] = useState({
    courseid: course?.courseid || null,
    coursename: course?.coursename || '',
    rating: course?.rating || 0,
    description: course?.description || '',
    price: course?.price !== undefined && course?.price !== null ? course.price : '',
    visible: course?.visible !== undefined ? course.visible : true,
    catid: course?.catid || '',
    duration: course?.duration || '',
    levelid: course?.levelid || '',
    students: course?.students || 0,
    imgurl: course?.imgurl || course?.image || '',
    syllabus: course?.syllabus || []
  });

  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [errors, setErrors] = useState({
    coursename: '', catid: '', levelid: '', price: '',
    rating: '', duration: '', description: ''
  });

  const [touched, setTouched] = useState({
    coursename: false, catid: false, levelid: false, price: false,
    rating: false, duration: false, description: false
  });

  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [hasLoadedCategories, setHasLoadedCategories] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [levels, setLevels] = useState([]);
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);
  const [levelError, setLevelError] = useState('');
  const [hasLoadedLevels, setHasLoadedLevels] = useState(false);
  const [isAddingLevel, setIsAddingLevel] = useState(false);
  const [newLevelName, setNewLevelName] = useState('');
  const [isCreatingLevel, setIsCreatingLevel] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [levelSearch, setLevelSearch] = useState('');
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const levelDropdownRef = useRef(null);

  const [categoriesWithIds, setCategoriesWithIds] = useState([]);
  const [levelsWithIds, setLevelsWithIds] = useState([]);

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragAreaRef = useRef(null);



  useEffect(() => {
    if (formData.imgurl && formData.imgurl !== '') {
      const fullUrl = getFullImageUrl(formData.imgurl);
      if (fullUrl) {
        setImagePreviewUrl(fullUrl);
      } else {
        setImagePreviewUrl(null);
      }
    } else {
      setImagePreviewUrl(null);
    }
  }, [formData.imgurl]);

  useEffect(() => {
    const loadData = async () => {
      await fetchCategories();
      await fetchLevels();
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!isNew && formData.catid && categoriesWithIds.length > 0) {
      const foundCategory = categoriesWithIds.find(cat => String(cat.id) === String(formData.catid));
      if (foundCategory && foundCategory.name) {
        setCategorySearch(foundCategory.name);
      }
    }
  }, [categoriesWithIds, formData.catid, isNew]);

  useEffect(() => {
    if (!isNew && formData.levelid && levelsWithIds.length > 0) {
      const foundLevel = levelsWithIds.find(level => String(level.id) === String(formData.levelid));
      if (foundLevel && foundLevel.name) {
        setLevelSearch(foundLevel.name);
      }
    }
  }, [levelsWithIds, formData.levelid, isNew]);

  useEffect(() => {
    if (formData.catid && categoriesWithIds.length > 0) {
      const foundCategory = categoriesWithIds.find(cat => String(cat.id) === String(formData.catid));
      if (foundCategory && foundCategory.name && categorySearch !== foundCategory.name) {
        setCategorySearch(foundCategory.name);
      }
    } else if (!formData.catid) {
      setCategorySearch('');
    }
  }, [formData.catid, categoriesWithIds]);

  useEffect(() => {
    if (formData.levelid && levelsWithIds.length > 0) {
      const foundLevel = levelsWithIds.find(level => String(level.id) === String(formData.levelid));
      if (foundLevel && foundLevel.name && levelSearch !== foundLevel.name) {
        setLevelSearch(foundLevel.name);
      }
    } else if (!formData.levelid) {
      setLevelSearch('');
    }
  }, [formData.levelid, levelsWithIds]);

  const handleOpenCurriculum = () => {
    if (!formData.courseid) {
      toast.info("Please save the course first before adding curriculum");
      return;
    }
    onViewCurriculum(formData);
  };

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'coursename':
        if (!value || value.trim() === '') {
          error = 'Course name is required';
        }
        break;
      case 'catid':
        if (!value) {
          error = 'Please select a category';
        }
        break;
      case 'levelid':
        if (!value) {
          error = 'Please select a level';
        }
        break;
      case 'price':
        if (value !== '' && value !== null && value !== undefined) {
          if (value < 0) {
            error = 'Price cannot be negative';
          }
        }
        break;
      case 'rating':
        if (value === '' || value === null || value === undefined) {
          error = 'Rating is required';
        } else if (value < 0 || value > 5) {
          error = 'Rating must be between 0 and 5';
        } else if (!Number.isInteger(value)) {
          error = 'Rating must be a whole number';
        }
        break;
      case 'duration':
        if (!value || value.trim() === '') {
          error = 'Duration is required';
        } else if (value.length < 2) {
          error = 'Please enter a valid duration (e.g., 12h30m, 8 weeks)';
        }
        break;
      case 'description':
        if (!value || value.trim() === '') {
          error = 'Description is required';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateForm = () => {
    const allTouched = {
      coursename: true, catid: true, levelid: true, price: true,
      rating: true, duration: true, description: true
    };
    setTouched(allTouched);

    const fieldsToValidate = ['coursename', 'catid', 'levelid', 'price', 'rating', 'duration', 'description'];
    let isValid = true;

    fieldsToValidate.forEach(field => {
      const value = formData[field];
      const isFieldValid = validateField(field, value);
      if (!isFieldValid) isValid = false;
    });

    return isValid;
  };

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    setCategoryError('');

    try {
      const response = await postRequest(apiUrls.getcategory, {});
      console.log("Full Categories response:", response);

      if (response.status === "S" && response.data) {
        let extractedCategories = [];
        if (Array.isArray(response.data)) {
          if (response.data.length > 0) {
            if (response.data[0]?.catid && response.data[0]?.catname) {
              extractedCategories = response.data.map(cat => ({ id: cat.catid, name: cat.catname }));
            }
          }
        }
        console.log("Extracted categories with IDs:", extractedCategories);
        setCategoriesWithIds(extractedCategories);
        setCategories(extractedCategories.map(cat => cat.name));
        setHasLoadedCategories(true);
      } else {
        const errorMsg = response.message || "Failed to load categories";
        setCategoryError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      const errorMsg = "Failed to load categories";
      setCategoryError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const createNewCategory = async (nameToCreate) => {
    const categoryName = nameToCreate || newCategoryName.trim();
    if (!categoryName) {
      toast.error("Please enter a category name");
      return;
    }
    setIsCreatingCategory(true);

    try {
      const response = await postRequest(apiUrls.createcategory, { catname: categoryName });
      console.log("Create category response:", response);

      if (response.status === "S") {
        await fetchCategories();
        setIsAddingCategory(false);
        setNewCategoryName('');
        toast.success(response.message || "Category created successfully");
      } else if (response.status === "E") {
        toast.error(response.message || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Failed to create category. Please try again.");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const fetchLevels = async () => {
    setIsLoadingLevels(true);
    setLevelError('');

    try {
      const response = await postRequest(apiUrls.getlevels, {});
      console.log("Full Levels response:", response);

      if (response.status === "S" && response.data) {
        let extractedLevels = [];

        if (Array.isArray(response.data)) {
          if (response.data.length > 0) {
            if (response.data[0].levelid && response.data[0].levelname) {
              extractedLevels = response.data.map(level => ({
                id: level.levelid,
                name: level.levelname
              }));
            }
          }
        }
        setLevelsWithIds(extractedLevels);
        setLevels(extractedLevels.map(level => level.name));
        setHasLoadedLevels(true);
      } else {
        const errorMsg = response.message || "Failed to load levels";
        setLevelError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching levels:", error);
      const errorMsg = "Failed to load levels";
      setLevelError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoadingLevels(false);
    }
  };

  const createNewLevel = async (nameToCreate) => {
    const levelName = nameToCreate || newLevelName.trim();
    setIsCreatingLevel(true);

    try {
      const response = await postRequest(apiUrls.createlevel, { levelname: levelName });
      console.log("Create level response:", response);

      if (response.status === "S") {
        await fetchLevels();
        setIsAddingLevel(false);
        setNewLevelName('');
        toast.success(response.message || "Level created successfully");
      } else if (response.status === "E") {
        toast.error(response.message || "Failed to create level");
      }
    } catch (error) {
      console.error("Error creating level:", error);
      toast.error("Failed to create level. Please try again.");
    } finally {
      setIsCreatingLevel(false);
    }
  };

  const processFile = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, WEBP, GIF)');
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setIsUploadingImage(true);

    try {
      const localPreviewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(localPreviewUrl);
      setImageFile(file);
      setFormData(prev => ({ ...prev, imgurl: '', image: file }));
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    await processFile(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData(prev => ({ ...prev, imgurl: '', image: null }));
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImagePreviewUrl(null);
    toast.info('Image removed');
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    await processFile(file);
  };

  const handleCategoryFocus = async () => {
    setIsCategoryDropdownOpen(true);
    if (!hasLoadedCategories || categoryError) {
      await fetchCategories();
    }
  };

  const handleLevelFocus = async () => {
    setIsLevelDropdownOpen(true);
    if (!hasLoadedLevels || levelError) {
      await fetchLevels();
    }
  };

  const filteredCategories = categoriesWithIds.filter(category => {
    if (!categorySearch) return true;
    return category.name.toLowerCase().includes(categorySearch.toLowerCase());
  });

  const filteredLevels = levelsWithIds.filter(level => {
    if (!levelSearch) return true;
    return level.name.toLowerCase().includes(levelSearch.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
        setIsAddingCategory(false);
        setNewCategoryName('');
      }
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(event.target)) {
        setIsLevelDropdownOpen(false);
        setIsAddingLevel(false);
        setNewLevelName('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleCategorySelect = (category) => {
    handleChange('catid', category.id);
    setCategorySearch(category.name);
    setIsCategoryDropdownOpen(false);
    if (touched.catid) {
      validateField('catid', category.id);
    }
  };

  const handleLevelSelect = (level) => {
    handleChange('levelid', level.id);
    setLevelSearch(level.name);
    setIsLevelDropdownOpen(false);
    if (touched.levelid) {
      validateField('levelid', level.id);
    }
  };

  const clearCategorySearch = () => {
    setCategorySearch('');
    handleChange('catid', '');
  };

  const clearLevelSearch = () => {
    setLevelSearch('');
    handleChange('levelid', '');
  };

  const handleSaveCurriculum = (updatedSyllabus) => {
    console.log("Saving curriculum:", updatedSyllabus);
    setFormData(prev => ({ ...prev, syllabus: updatedSyllabus }));
    toast.success("Curriculum updated successfully");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsUploadingImage(true);
      try {
        const formDataToSend = new FormData();

        formDataToSend.append('coursename', formData.coursename);
        formDataToSend.append('catid', formData.catid || '');
        formDataToSend.append('rating', formData.rating || 0);
        formDataToSend.append('description', formData.description || '');
        const priceValue = formData.price === '' ? 0 : (formData.price || 0);
        formDataToSend.append('price', priceValue);
        formDataToSend.append('invisible', !formData.visible ? 1 : 0);
        formDataToSend.append('levelid', formData.levelid || '');
        formDataToSend.append('duration', formData.duration || '');
        formDataToSend.append('students', formData.students || 0);
        formDataToSend.append('syllabus', JSON.stringify(formData.syllabus || []));

        if (formData.courseid) {
          formDataToSend.append('courseid', formData.courseid);
        }

        if (imageFile) {
          formDataToSend.append('image', imageFile);
        } else if (formData.imgurl && !imageFile) {
          formDataToSend.append('imgurl', formData.imgurl);
        }

        await onSave(formDataToSend);
      } catch (error) {
        console.error('Error in handleSubmit:', error);
      } finally {
        setIsUploadingImage(false);
      }
    } else {
      const firstErrorField = document.querySelector('.border-red-500');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      toast.error('Please fix all validation errors');
    }
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-120px)] bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
        <div className="w-full lg:w-1/2 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <button
                  onClick={onCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    {isNew ? 'Create New Course' : 'Edit Course'}
                  </h2>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleOpenCurriculum}
                  className="px-3 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg flex items-center gap-1"
                >
                  <BookOpen size={14} />
                  Syllabus
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isUploadingImage}
                  className="px-3 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploadingImage ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                  {isUploadingImage ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex gap-5 items-start">
                <div className="flex-shrink-0 mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Course Picture</label>

                  <div
                    ref={dragAreaRef}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`w-32 h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden ${isDragging
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                      } ${isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => !isUploadingImage && fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />

                    {imagePreviewUrl ? (
                      <div className="relative w-full h-full group">
                        <img
                          src={imagePreviewUrl}
                          alt="Course preview"
                          className="w-full h-full object-cover rounded-md"
                          onError={(e) => {
                            console.error('Image failed to load in editor:', imagePreviewUrl);
                            e.target.src = 'https://via.placeholder.com/128x128?text=Error';
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      <>
                        {isUploadingImage ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader size={24} className="animate-spin text-orange-500" />
                            <span className="text-xs text-gray-500">Uploading...</span>
                          </div>
                        ) : (
                          <>
                            <Upload size={24} className="text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500 text-center px-2">
                              Drag & drop
                            </span>
                            <span className="text-xs text-gray-400">or</span>
                            <span className="text-xs text-orange-500 font-medium">
                              Browse files
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <div className="mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Course Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.coursename}
                      onChange={(e) => handleChange('coursename', e.target.value)}
                      onBlur={() => handleBlur('coursename')}
                      className={`w-full text-sm border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.coursename && touched.coursename ? 'border-red-500' : 'border-gray-200'
                        }`}
                      placeholder="Enter course name"
                    />
                    {errors.coursename && touched.coursename && (
                      <p className="mt-1 text-xs text-red-500">{errors.coursename}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Price
                      </label>
                      <div className="relative">
                        <IndianRupee size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          value={formData.price === 0 ? '0' : (formData.price || '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              handleChange('price', '');
                            } else {
                              const numValue = Number(value);
                              if (!isNaN(numValue)) {
                                handleChange('price', numValue);
                              }
                            }
                          }}
                          onBlur={() => handleBlur('price')}
                          className={`w-full pl-9 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.price && touched.price ? 'border-red-500' : 'border-gray-200'
                            }`}
                          min="0"
                          step="1"
                          placeholder="0"
                        />
                      </div>
                      {errors.price && touched.price && (
                        <p className="mt-1 text-xs text-red-500">{errors.price}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Rating (0-5) <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="number"
                          value={formData.rating}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (value === '') {
                              handleChange('rating', '');
                            } else {
                              let numValue = Number(value);
                              if (!isNaN(numValue)) {
                                numValue = Math.round(numValue);
                                numValue = Math.min(5, Math.max(0, numValue));
                                handleChange('rating', numValue);
                              }
                            }
                          }}
                          onBlur={() => handleBlur('rating')}
                          step="1"
                          min="0"
                          max="5"
                          className={`w-20 text-sm border rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.rating && touched.rating ? 'border-red-500' : 'border-gray-200'
                            }`}
                        />
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              className={`${i < Math.floor(formData.rating)
                                ? 'fill-orange-500 text-orange-500'
                                : 'fill-gray-200 text-gray-200'}`}
                            />
                          ))}
                        </div>
                      </div>
                      {errors.rating && touched.rating && (
                        <p className="mt-1 text-xs text-red-500">{errors.rating}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Duration <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', e.target.value)}
                  onBlur={() => handleBlur('duration')}
                  className={`w-full text-sm border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${errors.duration && touched.duration ? 'border-red-500' : 'border-gray-200'
                    }`}
                  placeholder="e.g., 12h30m, 8 weeks"
                />
                {errors.duration && touched.duration && (
                  <p className="mt-1 text-xs text-red-500">{errors.duration}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={categoryDropdownRef}>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => {
                          setCategorySearch(e.target.value);
                          setIsCategoryDropdownOpen(true);
                          setIsAddingCategory(false);
                          if (e.target.value !== categorySearch) {
                            handleChange('catid', '');
                          }
                        }}
                        onFocus={handleCategoryFocus}
                        onBlur={() => handleBlur('catid')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const exactMatch = categoriesWithIds.find(c =>
                              c.name.toLowerCase() === categorySearch.toLowerCase()
                            );
                            if (categorySearch && !exactMatch) {
                              createNewCategory(categorySearch);
                            }
                          }
                        }}
                        placeholder="Select a category..."
                        className={`w-full pl-9 pr-8 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer ${errors.catid && touched.catid ? 'border-red-500' : 'border-gray-200'
                          }`}
                      />
                      {isLoadingCategories && (
                        <Loader size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" />
                      )}
                      {(categorySearch || formData.catid) && !isLoadingCategories && (
                        <button
                          type="button"
                          onClick={clearCategorySearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {errors.catid && touched.catid && (
                      <p className="mt-1 text-xs text-red-500">{errors.catid}</p>
                    )}

                    {isCategoryDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                        <div className="max-h-50 overflow-y-auto">
                          {isLoadingCategories ? (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                              <Loader size={16} className="animate-spin" />
                              Loading categories...
                            </div>
                          ) : categoryError ? (
                            <div className="px-4 py-3 text-sm text-red-500 text-center">
                              {categoryError}
                              <button
                                onClick={fetchCategories}
                                className="ml-2 text-orange-500 hover:text-orange-600 underline"
                              >
                                Retry
                              </button>
                            </div>
                          ) : (
                            <>
                              {categorySearch && !categoriesWithIds.some(c =>
                                c.name.toLowerCase() === categorySearch.toLowerCase()
                              ) && !isAddingCategory && (
                                  <div
                                    onClick={() => createNewCategory(categorySearch)}
                                    className="px-4 py-2.5 text-sm cursor-pointer transition-colors hover:bg-orange-50 border-b border-gray-100 text-orange-600 font-medium flex items-center gap-2"
                                  >
                                    <Plus size={16} />
                                    Add "{categorySearch}" as New Category
                                  </div>
                                )}

                              {filteredCategories.length > 0 ? (
                                filteredCategories.map((category) => (
                                  <div
                                    key={category.id}
                                    onClick={() => handleCategorySelect(category)}
                                    className={`px-4 py-2.5 text-sm cursor-pointer transition-colors hover:bg-orange-50 ${formData.catid === category.id
                                      ? 'bg-orange-50 text-orange-600 font-medium'
                                      : 'text-gray-700'
                                      }`}
                                  >
                                    {category.name}
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                  {categorySearch ? 'No matching categories found' : 'No categories available'}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Level <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={levelDropdownRef}>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={levelSearch}
                        onChange={(e) => {
                          setLevelSearch(e.target.value);
                          setIsLevelDropdownOpen(true);
                          setIsAddingLevel(false);
                          if (e.target.value !== levelSearch) {
                            handleChange('levelid', '');
                          }
                        }}
                        onFocus={handleLevelFocus}
                        onBlur={() => handleBlur('levelid')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const exactMatch = levelsWithIds.find(l =>
                              l.name.toLowerCase() === levelSearch.toLowerCase()
                            );
                            if (levelSearch && !exactMatch) {
                              createNewLevel(levelSearch);
                            }
                          }
                        }}
                        placeholder="Select a level..."
                        className={`w-full pl-9 pr-8 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer ${errors.levelid && touched.levelid ? 'border-red-500' : 'border-gray-200'
                          }`}
                      />
                      {isLoadingLevels && (
                        <Loader size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" />
                      )}
                      {(levelSearch || formData.levelid) && !isLoadingLevels && (
                        <button
                          type="button"
                          onClick={clearLevelSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {errors.levelid && touched.levelid && (
                      <p className="mt-1 text-xs text-red-500">{errors.levelid}</p>
                    )}

                    {isLevelDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                        <div className="max-h-50 overflow-y-auto">
                          {isLoadingLevels ? (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                              <Loader size={16} className="animate-spin" />
                              Loading levels...
                            </div>
                          ) : levelError ? (
                            <div className="px-4 py-3 text-sm text-red-500 text-center">
                              {levelError}
                              <button onClick={fetchLevels}
                                className="ml-2 text-orange-500 hover:text-orange-600 underline"
                              >Retry</button>
                            </div>
                          ) : (
                            <>
                              {levelSearch && !levelsWithIds.some(l =>
                                l.name.toLowerCase() === levelSearch.toLowerCase()
                              ) && !isAddingLevel && (
                                  <div
                                    onClick={() => createNewLevel(levelSearch)}
                                    className="px-4 py-2.5 text-sm cursor-pointer transition-colors hover:bg-orange-50 border-b border-gray-100 text-orange-600 font-medium flex items-center gap-2"
                                  >
                                    <Plus size={16} />
                                    Add "{levelSearch}" as New Level
                                  </div>
                                )}

                              {filteredLevels.length > 0 ? (
                                filteredLevels.map((level) => (
                                  <div
                                    key={level.id}
                                    onClick={() => handleLevelSelect(level)}
                                    className={`px-4 py-2.5 text-sm cursor-pointer transition-colors hover:bg-orange-50 ${formData.levelid === level.id
                                      ? 'bg-orange-50 text-orange-600 font-medium'
                                      : 'text-gray-700'
                                      }`}
                                  >
                                    {level.name}
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                  {levelSearch ? 'No matching levels found' : 'No levels available'}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  onBlur={() => handleBlur('description')}
                  rows={4}
                  className={`w-full h-[20vh] text-sm border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-y ${errors.description && touched.description ? 'border-red-500' : 'border-gray-200'
                    }`}
                  placeholder="Enter course description ..."
                />
                {errors.description && touched.description && (
                  <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                )}
              </div>
            </form>
          </div>
        </div>
        <div className="w-full lg:w-1/2 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden p-4 sm:p-6 lg:p-8 flex flex-col items-center justify-center">
          <div className="w-full max-w-md">
            <div className="mt-2 flex items-center justify-center gap-2">
              <Eye size={14} className="text-gray-400" />
              <h3 className="text-xs font-medium text-gray-400">Live Preview</h3>
            </div>
            <div className="scale-95 transform">
              <PreviewCard formData={formData} onViewCurriculum={handleOpenCurriculum} />
            </div>
          </div>
        </div>
      </div>

      <CommonToastContainer />
    </>
  );
};

export default CourseDetails;